
import React, { useState, useCallback, useEffect } from 'react';
import { format } from 'date-fns';
import TableComponent from '../../../components/Table/TableComponent';

import SearchIcon from '@mui/icons-material/Search';
import { TableRow, TableCell } from '@mui/material';
import TablePaginationComponent from '../../../components/TablePaginationComponent';
import DateRangePicker from '../../../components/FormFilelds/DateRangePicker';

import { Diet } from '../../../components/interface/SubjectiveInterfaces/DietInterface';
import { useAuth } from '../../../context/AuthContext';
import { fetchDiets } from '../../../services/SubjectiveServices/DietformService';
import IconButton, { ClearButton } from '../../../components/Buttons/SubjectiveIcons';
import SnackbarMessage from '../../../components/SnackBAr/SnackbarNotification';
import LoadingAnimation from '../../../components/LoadingAnimation';



interface DietFormProps {
    patientId: string;
}

const DietForm: React.FC<DietFormProps> = ({ patientId }) => {
    const { token } = useAuth();
    const [diets, setDiets] = useState<Diet[]>([]);
    const [totalDiets, setTotalDiets] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [dateRange, setDateRange] = useState<{ start: string; end: string }>({ start: '', end: '' });
    const [isButtonEnabled, setIsButtonEnabled] = useState(false);


    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'info' | 'warning' | 'error'>('success');

    const [showClearFilterButton, setShowClearFilterButton] = useState(false);

    const showSnackbar = (message: string, severity: 'success' | 'info' | 'warning' | 'error') => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    const fetchDietsData = useCallback(async (initial = false) => {
        setLoading(true);
        setError('');

        try {
            // Add a delay of 1 second
            await new Promise(resolve => setTimeout(resolve, 300));

            const params: Record<string, any> = {
                limit: rowsPerPage,
                page: page + 1,
                order_by: 'values->date',
                dir: 2,
                user_id: patientId,
                slug: 'diet',
                searchkey: searchTerm,
                slug_array: ''
            };

            if (!initial) {
                params.from = dateRange.start;
                params.to = dateRange.end;
            }

            const data = await fetchDiets(token!, params);
            const fetchedDiets = data.data.patient_healths.map(item => ({
                id: item.id,
                slug: item.slug,
                consult_id: item.consult_id,
                values: {
                    date: item.values.date,
                    time: item.values.time,
                    notes: item.values.notes,
                    category: item.values.category,
                    nutritian_values: item.values.nutritian_values,
                },
                created_at: item.created_at,
                freeze: item.freeze
            }));

            setDiets(fetchedDiets);
            setTotalDiets(data.data.pagination.total || 0);
            setShowClearFilterButton(!initial || searchTerm.trim() !== '' || (dateRange.start !== '' && dateRange.end !== ''));

        } catch (error) {
            setError('Failed to fetch diets.');
            console.error('Error fetching diets', error);
            showSnackbar('Failed to fetch diets.', 'error');
        } finally {
            setLoading(false);
        }
    }, [searchTerm, dateRange, page, rowsPerPage, patientId]);


    useEffect(() => {
        fetchDietsData(true);
    }, [fetchDietsData]);

    useEffect(() => {
        const isSearchable = searchTerm.trim() !== '' || (dateRange.start !== '' && dateRange.end !== '');
        setIsButtonEnabled(isSearchable);
    }, [searchTerm, dateRange]);

    const handleSearch = useCallback(() => {
        if (dateRange.start && dateRange.end) {
            setPage(0);
            setLoading(true);
            fetchDietsData().finally(() => setLoading(false));
        } else {
            console.warn('Please select both from date and to date.');
            showSnackbar('Please select both from date and to date.', 'warning');
        }
    }, [fetchDietsData, dateRange]);

    const handleButtonClick = () => {
        handleSearch();
        // setDateRange({ start: '', end: '' });
    };

    const handleClearFilters = () => {
        setSearchTerm('');
        setDateRange({ start: '', end: '' });
        setPage(0);
        fetchDietsData(true);
    };


    const formatDate = (dateString: string | number | Date) => {
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                return 'Invalid Date'; 
            }
            return format(date, 'dd-MM-yyyy');
        } catch (error) {
            console.error("Error formatting date:", error);
            return 'Invalid Date'; 
        }
    };
    const renderRow = (item: Diet, index: number) => (
        <TableRow key={item.id || index}>
            <TableCell className="px-4 py-3 font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}> {page * rowsPerPage + index + 1}</TableCell>
            <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>{`${formatDate(item.values.date)} ${item.values.time}`}</TableCell>
            <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>{item.values.category}</TableCell>
            <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>{item.values.notes}</TableCell>
            {/* <TableCell>{item.calories}</TableCell> */}
        </TableRow>
    );

    return (
        <div>
            <div className="flex items-end justify-between w-full">
                <div className="flex flex-grow space-x-4">
                    <DateRangePicker
                        startDateId="diet_Start_Date"
                        endDateId="diet_End_Date"
                        value={{ startDate: dateRange.start, endDate: dateRange.end }}
                        onChange={dates => {
                            setDateRange({ start: dates.startDate || '', end: dates.endDate || '' });
                        }}
                    />
                    <IconButton
                        onClick={handleButtonClick}
                        disabled={!isButtonEnabled}
                        icon={<SearchIcon />}
                    />
                    {showClearFilterButton && (
                        <ClearButton onClick={handleClearFilters} />
                    )}
                </div>
            </div>
            <div className="relative">
                {loading && (
                    <div className="absolute inset-0 flex justify-center items-center bg-white/60 z-10"> {/* Flexbox centering */}
                        <LoadingAnimation />
                    </div>
                )}
                {loading ? (
                    <div className="text-center my-4">Loading...</div>
                ) : (
                    <div>
                        <TableComponent
                            headers={['S.No','Date&Time', 'TypeofDiet', 'Notes']}
                            data={diets}
                            loading={loading}
                            error={error}
                            renderRow={renderRow}
                        />

                    </div>
                )}

            </div>
            <TablePaginationComponent
                count={totalDiets}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={(_event, newPage) => {
                    setPage(newPage);
                }}
                onRowsPerPageChange={(event) => {
                    const newRowsPerPage = parseInt(event.target.value, 10);
                    setRowsPerPage(newRowsPerPage);
                    setPage(0);
                }}
            />
            {/* Snackbar Component */}
            <SnackbarMessage
                open={snackbarOpen}
                message={snackbarMessage}
                severity={snackbarSeverity}
                onClose={() => setSnackbarOpen(false)}
            />
        </div>
    );
};

export default DietForm;
